import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Button, DialogActions } from '@mui/material';
import { useIsBelowMd } from 'hooks/use-is-below-md';
import {
  PickersActionBarAction,
  PickersActionBarProps,
} from '@mui/x-date-pickers/PickersActionBar';
import { usePickerContext, usePickerTranslations } from '@mui/x-date-pickers/hooks';
import { PickerChangeHandlerContext } from '@mui/x-date-pickers/models';
import { heIL } from '@mui/x-date-pickers/locales';
import style from './index.module.scss';

/**
 * The @material-ui/pickers 3 open / close model for the app's dialog pickers
 * (prompt 2a-fix, R-20 / R-19; D-48).
 *
 * v3's default (`variant="dialog"`) was a modal `Dialog` on every pointer
 * type with a Cancel / OK bar (Today where `showTodayButton`): a day click
 * moved the dialog's own date and nothing else, OK published it (`onChange`,
 * then `onAccept`, then close), Cancel and a backdrop click discarded it,
 * Today moved the dialog's date without closing. Typing in the field
 * published `onChange` on every accepted keystroke and never `onAccept`.
 *
 * x-date-pickers differs on each point: `onChange` fires on the day click,
 * a backdrop click accepts, Today accepts and closes, Cancel reverts to the
 * value the picker had at *mount* (`lastCommittedValue`), and a field edit
 * with the default change importance fires `onAccept`.
 *
 * `usePickerModal` puts the v3 model back at the wrapper level so the call
 * sites inherit it unchanged: while the dialog is open the picker renders a
 * draft only the wrapper holds, and `AppPickerActionBar` (the picker's
 * `slots.actionBar`) commits or discards it through the context below. The
 * dialog's own close (backdrop, Escape) is routed to the same discard.
 *
 * Only single-value pickers use it (`Date | null`); the app's range picker is
 * its own component and keeps its Cancel-reverts model (R-22, D-48).
 */
type Value = Date | null;
type ChangeContext<TError> = PickerChangeHandlerContext<TError>;
type ChangeHandler<TError> = (value: Value, context: ChangeContext<TError>) => void;

interface Actions {
  /** OK: publish the draft to the consumer (`onChange`, then `onAccept`) and close. */
  commit: () => void;
  /** Today: move the draft, keep the dialog open (v3 `onSetToday`). */
  setToday: () => void;
}

const PickerModalContext = createContext<Actions | null>(null);

/** The context a picker reports for an untouched value (no selection yet). */
const untouchedContext = <TError extends string | null>(): ChangeContext<TError> => ({
  validationError: null as TError,
  source: 'view',
});

/** The dialog's own date while it is open, with the context of the selection that set it. */
interface Draft<TError> {
  value: Value;
  context: ChangeContext<TError>;
}

export interface UsePickerModalOptions<TError> {
  /** The picker value the wrapper derived from the consumer's `value`. */
  value: Value;
  /** The consumer-facing publish. */
  onChange: ChangeHandler<TError>;
  onAccept?: ChangeHandler<TError>;
  /** v3 `autoOk`: the first finishing selection commits and closes (x-date-pickers `closeOnSelect`). */
  autoOk?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

export interface PickerModalProps<TError> {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  value: Value;
  onChange: ChangeHandler<TError>;
  onAccept: ChangeHandler<TError>;
  closeOnSelect: boolean;
}

export const usePickerModal = <TError extends string | null>({
  value,
  onChange,
  onAccept,
  autoOk = false,
  onOpen,
  onClose,
}: UsePickerModalOptions<TError>) => {
  // The draft doubles as the open state: `null` while closed, the dialog's own date while open.
  const [draft, setDraft] = useState<Draft<TError> | null>(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  // The latest consumer callbacks, so the picker's memoised slots never hold a stale one.
  const latest = useRef({ value, onChange, onAccept, onOpen, onClose });
  latest.current = { value, onChange, onAccept, onOpen, onClose };

  const close = useCallback(() => {
    setDraft(null);
    latest.current.onClose?.();
  }, []);

  const handleOpen = useCallback(() => {
    setDraft({ value: latest.current.value, context: untouchedContext() });
    latest.current.onOpen?.();
  }, []);

  const publish = useCallback<ChangeHandler<TError>>((next, context) => {
    latest.current.onChange(next, context);
    latest.current.onAccept?.(next, context);
  }, []);

  const actions = useMemo<Actions>(
    () => ({
      commit: () => {
        const current = draftRef.current;
        if (current) {
          publish(current.value, current.context);
        }
        close();
      },
      setToday: () =>
        setDraft((prev) =>
          prev ? { value: new Date(), context: { ...prev.context, source: 'view' } } : prev,
        ),
    }),
    [publish, close],
  );

  const handleChange = useCallback<ChangeHandler<TError>>((next, context) => {
    if (draftRef.current) {
      // A view selection while the dialog is open: the draft moves, the consumer does not hear it.
      setDraft({ value: next, context });
      return;
    }
    // Typing in the field (the dialog is closed): v3 published `onChange` per accepted keystroke.
    latest.current.onChange(next, context);
  }, []);

  // The picker's own accept reaches the wrapper only through `closeOnSelect`
  // (the dialog's backdrop / Escape close is rerouted to `close`, and the
  // action bar is the app's). It is v3's `autoOk`: commit and let the picker close.
  const handleAccept = useCallback<ChangeHandler<TError>>(
    (next, context) => {
      if (draftRef.current && autoOk) {
        publish(next, context);
      }
    },
    [autoOk, publish],
  );

  const pickerProps = useMemo<PickerModalProps<TError>>(
    () => ({
      open: draft !== null,
      onOpen: handleOpen,
      onClose: close,
      value: draft ? draft.value : value,
      onChange: handleChange,
      onAccept: handleAccept,
      closeOnSelect: autoOk,
    }),
    [draft, value, handleOpen, close, handleChange, handleAccept, autoOk],
  );

  /**
   * `slotProps.dialog`: the backdrop / Escape close discards the draft, as
   * v3's `onDismiss` did. Below `md` the dialog is full-screen (Phase F1,
   * Task 5b, D-54): `fullScreen` is MUI's own prop, so the picker's modal
   * paper follows the app's dialogs without touching the Cancel / OK / Today
   * model above. `useIsBelowMd` is false where `matchMedia` is missing (jsdom).
   */
  const isBelowMd = useIsBelowMd();
  // N2 (T5): below md the full-screen modal pins its actions to the bottom
  // edge (index.module.scss); the desktop dialog gets no class
  const dialogSlotProps = useMemo(
    () => ({
      onClose: close,
      fullScreen: isBelowMd,
      className: isBelowMd ? style.fullScreen : undefined,
    }),
    [close, isBelowMd],
  );
  // N2 (T5): the toolbar's title in Hebrew below md -- x-date-pickers' own
  // heIL strings, scoped to the mobile modal; the desktop keeps its title
  const localeTextBelowMd = isBelowMd ? PICKER_TOOLBAR_TITLES_HE : undefined;

  return { pickerProps, dialogSlotProps, actions, localeTextBelowMd };
};

const HE_LOCALE_TEXT = heIL.components.MuiLocalizationProvider.defaultProps.localeText;
/** The modal toolbar's titles, from x-date-pickers' Hebrew locale. */
const PICKER_TOOLBAR_TITLES_HE = {
  datePickerToolbarTitle: HE_LOCALE_TEXT.datePickerToolbarTitle,
  dateTimePickerToolbarTitle: HE_LOCALE_TEXT.dateTimePickerToolbarTitle,
};

/** Wraps the picker so `AppPickerActionBar` (rendered in the dialog's portal) finds the wrapper's actions. */
export const PickerModalProvider: React.FC<React.PropsWithChildren<{ actions: Actions }>> = ({
  actions,
  children,
}) => <PickerModalContext.Provider value={actions}>{children}</PickerModalContext.Provider>;

/** v3's modal bar order: Today (`showTodayButton`), Cancel, OK. */
export const PICKER_MODAL_ACTIONS: PickersActionBarAction[] = ['cancel', 'accept'];
export const PICKER_MODAL_ACTIONS_WITH_TODAY: PickersActionBarAction[] = [
  'today',
  'cancel',
  'accept',
];

/**
 * The picker's `slots.actionBar`. Same DOM as MUI's `PickersActionBar`
 * (`DialogActions` carrying the layout's `actionBar` class, text `Button`s
 * with the picker's translations) with the wrapper's handlers instead of the
 * picker's: Cancel closes and discards, OK commits, Today moves the draft.
 */
export const AppPickerActionBar = React.memo<PickersActionBarProps>(({ actions, ...other }) => {
  const modal = useContext(PickerModalContext);
  const picker = usePickerContext();
  const translations = usePickerTranslations();

  if (!modal) {
    throw new Error('AppPickerActionBar: rendered outside PickerModalProvider');
  }
  if (!actions || actions.length === 0) {
    return null;
  }

  const buttons = actions.map((action) => {
    switch (action) {
      case 'cancel':
        return (
          <Button key={action} onClick={() => picker.setOpen(false)}>
            {translations.cancelButtonLabel}
          </Button>
        );
      case 'accept':
        return (
          <Button key={action} onClick={modal.commit}>
            {translations.okButtonLabel}
          </Button>
        );
      case 'today':
        return (
          <Button key={action} onClick={modal.setToday}>
            {translations.todayButtonLabel}
          </Button>
        );
      default:
        return null;
    }
  });

  return <DialogActions {...other}>{buttons}</DialogActions>;
});
